import React, { useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { ConfirmModal, POSIcon } from '@publicisgroupe/p-os-components';
import { Dropzone } from 'components/Dropzone/Dropzone';
import FileConstraintsInfo from 'components/FileConstraintsInfo';
import { v4 as uuidv4 } from 'uuid';
import { ImageFileConstraints } from 'types/types';
import { useTranslation } from 'react-i18next';
import './DropzoneContainer.less';

interface DropzoneContainerProps {
  constraints: ImageFileConstraints;
  handleDeleteFile(file: File, constrains: ImageFileConstraints): void;
  handleAddFile(file: File[], constrains: ImageFileConstraints): void;
  files?: File[];
}

export const DropzoneContainer = ({
  constraints,
  files,
  handleDeleteFile,
  handleAddFile,
}: DropzoneContainerProps) => {
  const { t } = useTranslation();
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [fileToRemove, setFileToRemove] = useState<File>();
  const { acceptedFileTypes, maxFileSize } = constraints;

  const handleRejectedFiles = (fileRejections: FileRejection[]) => setRejectedFiles(fileRejections);

  const handleDropzoneFileAdded = (files: File[]) => {
    handleAddFile(files, constraints);
  };

  const handleDropzoneFileDeletion = (file?: File) => {
    setOpenConfirmModal(true);
    setFileToRemove(file as File);
  };

  const confirmRemovingFile = () => {
    if (fileToRemove) {
      handleDeleteFile(fileToRemove, constraints);
    }
    setOpenConfirmModal(false);
  };

  return (
    <>
      <FileConstraintsInfo {...constraints} rejectedFiles={rejectedFiles} />
      <div className="file-dropzone-container">
        {!files?.length && (
          <Dropzone
            acceptedFileTypes={acceptedFileTypes}
            maxFileSize={maxFileSize}
            getUploadedFiles={handleDropzoneFileAdded}
            getRejectedFiles={handleRejectedFiles}
          />
        )}

        {files
          ?.filter((file) => file !== null)
          ?.map((file) => {
            return (
              <div key={`${uuidv4()}`} className="file-upload-container-file">
                <span
                  className="file-upload-container-file-name"
                  data-testid="file-upload-file-name"
                >
                  {file.name}
                </span>
                <ConfirmModal
                  openConfirmModal={openConfirmModal}
                  headerContent={t('FileDeleteConfirmation')}
                  subHeaderContent={''}
                  primaryButtonText={t('FileDeleteApproval')}
                  secondaryButtonText={t('Cancel')}
                  setOpenConfirmModal={setOpenConfirmModal}
                  onConfirm={confirmRemovingFile}
                />
                <span className="file-upload-container-file-seperator"> | </span>
                <span
                  className="file-upload-container-file-action"
                  data-testid="file-upload-delete-action"
                >
                  <POSIcon
                    icon="delete"
                    className="file-upload-delete-action-icon"
                    onClick={() => handleDropzoneFileDeletion(file)}
                  />
                </span>
              </div>
            );
          })}
      </div>
    </>
  );
};
